import type { ReactNode } from "react";

import type { Category } from "@/features/categories/api/categoriesApi";
import { plural } from "@/lib/format";

export type CategoryGroup<T> = {
  code: string;
  name: string;
  items: T[];
};

/**
 * Groups rows by their category, in the order the API lists them, and drops
 * the empty ones. The catch-all lands last whatever its name, so a screen
 * never opens on "Autre".
 */
export function groupByCategory<T extends { typeCode?: string | null }>(
  items: T[],
  categories: Category[],
  fallbackCode: string,
): Array<CategoryGroup<T>> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const code = item.typeCode || fallbackCode;
    groups.set(code, [...(groups.get(code) ?? []), item]);
  }

  const ordered = [
    ...categories.filter((category) => category.code !== fallbackCode),
    ...categories.filter((category) => category.code === fallbackCode),
  ];

  const known = ordered
    .filter((category) => groups.has(category.code))
    .map((category) => ({
      code: category.code,
      name: category.name,
      items: groups.get(category.code)!,
    }));

  // A code with no matching category still has to show its rows somewhere.
  const orphans = Array.from(groups.entries())
    .filter(([code]) => !categories.some((category) => category.code === code))
    .map(([code, rows]) => ({ code, name: "Autre", items: rows }));

  return [...known, ...orphans];
}

export function CategoryGroups<T>({
  groups,
  countLabel,
  children,
}: {
  groups: Array<CategoryGroup<T>>;
  /** Singular noun counted in each header, e.g. "matière". */
  countLabel: string;
  children: (items: T[], group: CategoryGroup<T>) => ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <section key={group.code} className="flex flex-col gap-3">
          <div className="flex items-baseline gap-2">
            <h2 className="text-[15px] font-semibold">{group.name}</h2>
            <span className="text-sm text-muted-foreground tabular-nums">
              {group.items.length} {plural(group.items.length, countLabel)}
            </span>
          </div>
          {children(group.items, group)}
        </section>
      ))}
    </div>
  );
}
